// AssetProgressPage.jsx — 美术—程序资产对账表的四态记录（SB-197 S3b，第二百九十二批）。
//
// ── 它与台账页的分工 ────────────────────────────────────────────────────────
// 台账页（文档面）是**正典的呈现**：主表、用途绑定、§5 派生表，都由仓内 md／jsonl 生成。
// 这一页是**应用面**：同一棵巢状树（面 → 类型 → MA），但每一格可以**记**——记的是四态
// 事件（美术已交／程序已收／程序已接／画面已验），落进协调系统的追加式台账，工作空间
// 按批拉回正典，§5 随之更新。所以这里没有"改状态"，只有"追加一条带证据的记录"。
//
// ── 谁能记什么，由服务端说 ──────────────────────────────────────────────────
// GET /api/asset-states 回 `role`（岗位派生：art／program／admin；空串＝只读）。按钮的显隐
// 照它，但显隐只是礼貌——真正的闸在 POST（403），页面不替后端判权。
//
// ── supersedes 不由人填 ─────────────────────────────────────────────────────
// 每格的最新事件 id 就是 supersedes；别人刚记了一条 ⇒ 409 ⇒ 刷新再提。人不需要知道
// 这个字段存在。
//
// ── 缺省全部收起 ────────────────────────────────────────────────────────────
// 与台账派生视图同一条裁定（需求方 2026-09-22）：49 行的树展开即一屏放不下，先看进度、
// 再点开要记的那一面。收起的面**不挂载**（unmountOnExit）：49 行 × 4 态的单元格在收起时
// 不该占着 DOM——探针以「收起时页上没有 MA 单元格」判缺省收起，而不是看高度。
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Divider, MenuItem, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import HelpLink from '../components/HelpLink.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useT } from '../i18n/index.mjs';
import {
  STATES, VALUES, buildTree, detailPairs, errorKeyFor, groupFromSearch, historyOf, latestFor, mayRecord,
  plainText, submitPayload, usageLine, valueColor,
} from './assetProgress.mjs';

const day = (ms) => (Number.isFinite(ms) && ms > 0 ? new Date(ms).toISOString().slice(0, 10) : '');

function StateCell({ t, entry, state, role, onRecord }) {
  const e = entry.cells[state];
  return (
    <TableCell data-state={state} data-ma={entry.ma} sx={{ verticalAlign: 'top', minWidth: 150 }}>
      {e ? (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          <Chip size="small" color={valueColor(e.value)} label={t(`assetProgress.value.${e.value}`)} />
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {e.actorId} · {day(e.atMs)}{e.assetVersion ? ` · ${e.assetVersion}` : ''}
          </Typography>
          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{e.evidence}</Typography>
        </Stack>
      ) : <Typography variant="body2" color="text.secondary">{t('assetProgress.none')}</Typography>}
      {mayRecord(role, state) ? (
        <Button size="small" sx={{ mt: 0.5, minWidth: 0 }} data-record={`${entry.ma}|${state}`}
                onClick={() => onRecord(entry.ma, state)}>
          {t('assetProgress.record')}
        </Button>
      ) : null}
    </TableCell>
  );
}

function RecordDialog({ t, group, ma, state, latest, onClose, onDone }) {
  const cur = latestFor(latest, ma, state);
  const [value, setValue] = useState(cur ? cur.value : 'done');
  const [evidence, setEvidence] = useState('');
  const [assetVersion, setAssetVersion] = useState(cur ? cur.assetVersion || '' : '');
  const [usage, setUsage] = useState(cur ? cur.usage || '' : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const payload = submitPayload({ group, ma, state, value, evidence, assetVersion, usage, note, latest });

  const submit = async () => {
    if (!payload.ok) { setErr(t(payload.errorKey)); return; }
    setBusy(true);
    setErr('');
    try {
      await api.recordAssetState(payload.body);
      onDone();
    } catch (e) {
      const k = errorKeyFor(e);
      setErr(t(k));
      // 409：别人刚记了一条——把最新的拉回来，让下一次提交接在对的那条之后。
      if (k === 'assetProgress.err.stale') onDone({ keepOpen: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('assetProgress.dialog.title', { ma, state: t(`assetProgress.state.${state}`) })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {cur ? t('assetProgress.dialog.chain', { id: cur.id }) : t('assetProgress.dialog.chainFirst')}
          </Typography>
          <TextField select size="small" label={t('assetProgress.dialog.value')} value={value}
                     onChange={(e) => setValue(e.target.value)}>
            {VALUES.map((v) => <MenuItem key={v} value={v}>{t(`assetProgress.value.${v}`)}</MenuItem>)}
          </TextField>
          <TextField size="small" required id="asset-evidence" label={t('assetProgress.dialog.evidence')} value={evidence}
                     onChange={(e) => setEvidence(e.target.value)} />
          <TextField size="small" label={t('assetProgress.dialog.assetVersion')} value={assetVersion}
                     onChange={(e) => setAssetVersion(e.target.value)} />
          <TextField size="small" label={t('assetProgress.dialog.usage')} value={usage}
                     onChange={(e) => setUsage(e.target.value)} />
          <TextField size="small" label={t('assetProgress.dialog.note')} value={note}
                     onChange={(e) => setNote(e.target.value)} />
          {err ? <Alert severity="error">{err}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !payload.ok} data-submit="record">
          {t('assetProgress.dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AssetProgressPage() {
  const t = useT();
  const { search } = useLocation();
  const group = groupFromSearch(search);
  const { status, data, error, reload } = useLoad(() => api.assetStates({ group, rows: true }), [group]);
  const [dialog, setDialog] = useState(null);      // { ma, state }
  const [drawerMa, setDrawerMa] = useState(null);
  const [detailMa, setDetailMa] = useState(null);   // 第二百九十四批：MA 点开看台账明细
  const [expanded, setExpanded] = useState({});    // section → bool（缺省全部收起）

  const rows = data?.rows ?? [];
  const events = data?.events ?? [];
  const latest = data?.latest ?? {};
  const role = data?.role ?? '';
  const tree = useMemo(() => buildTree(rows, latest), [rows, latest]);
  const loading = status === 'loading';
  const errKeyTop = status === 'error' ? errorKeyFor(error) : '';

  const setAll = (open) => setExpanded(Object.fromEntries(tree.map((s) => [s.section, open])));
  const closeDialog = () => setDialog(null);
  const onDone = (opts) => { reload(); if (!opts?.keepOpen) setDialog(null); };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('assetProgress.title')}
        <HelpLink section="asset-progress" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('assetProgress.subtitle')}</Typography>

      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Chip size="small" color={role ? 'primary' : 'default'} variant={role ? 'filled' : 'outlined'}
              label={role ? t('assetProgress.role', { role }) : t('assetProgress.roleNone')} data-role={role} />
        {status === 'ready' ? (
          <Typography variant="body2" data-summary="rows">
            {t('assetProgress.summary', { rows: rows.length, events: events.length })}
          </Typography>
        ) : null}
        <Button size="small" onClick={() => setAll(true)}>{t('assetProgress.expandAll')}</Button>
        <Button size="small" onClick={() => setAll(false)}>{t('assetProgress.collapseAll')}</Button>
        <Button size="small" onClick={reload}>{t('assetProgress.reload')}</Button>
      </Stack>

      {loading ? <CircularProgress size={24} sx={{ my: 2 }} /> : null}
      {errKeyTop ? <Alert severity="error" sx={{ my: 2 }}>{t(errKeyTop)}</Alert> : null}
      {status === 'ready' && rows.length === 0
        ? <Alert severity="info" sx={{ my: 2 }}>{t('assetProgress.rowsEmpty')}</Alert> : null}

      {tree.map((sec) => (
        <Accordion key={sec.section} expanded={Boolean(expanded[sec.section])}
                   onChange={(_, open) => setExpanded((x) => ({ ...x, [sec.section]: open }))}
                   disableGutters data-section={sec.section}
                   slotProps={{ transition: { unmountOnExit: true } }}>
          <AccordionSummary sx={{ '& .MuiAccordionSummary-content': { flexWrap: 'wrap', gap: 1, alignItems: 'center' } }}>
            <Typography variant="subtitle1" sx={{ mr: 1 }}>{sec.section}</Typography>
            <Chip size="small" variant="outlined" label={t('assetProgress.sectionSummary', { count: sec.count })} />
            {STATES.map((s) => (
              <Chip key={s} size="small" variant="outlined"
                    color={sec.progress[s] === sec.progress.total && sec.progress.total > 0 ? 'success' : 'default'}
                    label={`${t(`assetProgress.state.${s}`)} ${t('assetProgress.done', { done: sec.progress[s], total: sec.progress.total })}`} />
            ))}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, overflowX: 'auto' }}>
            {sec.types.map((ty) => (
              <Box key={ty.type} sx={{ mb: 1 }}>
                <Typography variant="overline" sx={{ px: 2 }}>{ty.type} · {ty.rows.length}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('assetProgress.col.ma')}</TableCell>
                      <TableCell>{t('assetProgress.col.purpose')}</TableCell>
                      <TableCell>{t('assetProgress.col.deliverer')}</TableCell>
                      <TableCell>{t('assetProgress.col.version')}</TableCell>
                      {STATES.map((s) => <TableCell key={s}>{t(`assetProgress.state.${s}`)}</TableCell>)}
                      <TableCell>{t('assetProgress.col.history')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ty.rows.map((entry) => (
                      <TableRow key={entry.ma} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }} data-ma-cell={entry.ma}>
                          {/* 需求方 2026-09-23 指令：MA 可点开看资产详情（台账明细块，经镜像 detail 带来） */}
                          <Button size="small" sx={{ minWidth: 0, fontFamily: 'monospace', textTransform: 'none' }}
                                  data-ma-detail={entry.ma} onClick={() => setDetailMa(entry.ma)}>{entry.ma}</Button>
                        </TableCell>
                        {/* 第二百九十五批（需求方指令）：「用途」列＝主表用途首句 ＋ §3 用途绑定（流程 · 页面） */}
                        <TableCell sx={{ minWidth: 180, maxWidth: 320 }} data-purpose={entry.ma}>
                          <Typography variant="body2">{plainText(entry.row.purpose)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {usageLine(entry.row.usage) || t('assetProgress.usage.unbound')}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.row.deliverer}</TableCell>
                        <TableCell>{entry.row.version}</TableCell>
                        {STATES.map((s) => (
                          <StateCell key={s} t={t} entry={entry} state={s} role={role}
                                     onRecord={(ma, state) => setDialog({ ma, state })} />
                        ))}
                        <TableCell>
                          <Button size="small" sx={{ minWidth: 0 }} data-history={entry.ma}
                                  onClick={() => setDrawerMa(entry.ma)}>{t('assetProgress.col.history')}</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {dialog ? (
        <RecordDialog key={`${dialog.ma}|${dialog.state}|${data?.lastId ?? 0}`} t={t} group={group}
                      ma={dialog.ma} state={dialog.state} latest={latest}
                      onClose={closeDialog} onDone={onDone} />
      ) : null}

      {detailMa ? (() => {
        const row = rows.find((x) => x.ma === detailMa);
        const pairs = detailPairs(row);
        return (
          <Dialog open onClose={() => setDetailMa(null)} fullWidth maxWidth="md" data-detail-dialog={detailMa}>
            <DialogTitle>{t('assetProgress.detail.title', { ma: detailMa })}</DialogTitle>
            <DialogContent>
              {row ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {row.section} · {row.type} · {row.version} · {row.deliverer}
                </Typography>
              ) : null}
              {pairs.length === 0
                ? <Alert severity="info">{t('assetProgress.detail.empty')}</Alert>
                : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('assetProgress.col.field')}</TableCell>
                        <TableCell>{t('assetProgress.col.value')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pairs.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{p.k}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{p.v}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailMa(null)}>{t('common.close')}</Button>
            </DialogActions>
          </Dialog>
        );
      })() : null}

      <Drawer anchor="right" open={Boolean(drawerMa)} onClose={() => setDrawerMa(null)}
              PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 2 } }}>
        <Typography variant="h6" gutterBottom>{t('assetProgress.history.title', { ma: drawerMa ?? '' })}</Typography>
        <Divider sx={{ mb: 1 }} />
        {drawerMa && historyOf(events, drawerMa).length === 0
          ? <Typography variant="body2" color="text.secondary">{t('assetProgress.history.empty')}</Typography> : null}
        <Stack spacing={1.5}>
          {drawerMa ? historyOf(events, drawerMa).map((e) => (
            <Box key={e.id} data-event={e.id}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>#{e.id}</Typography>
                <Chip size="small" variant="outlined" label={t(`assetProgress.state.${e.state}`)} />
                <Chip size="small" color={valueColor(e.value)} label={t(`assetProgress.value.${e.value}`)} />
                {e.supersedes ? <Typography variant="caption" color="text.secondary">← #{e.supersedes}</Typography> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {e.actorId} ({e.actorRole}) · {day(e.atMs)}{e.assetVersion ? ` · ${e.assetVersion}` : ''}{e.usage ? ` · ${e.usage}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{e.evidence}</Typography>
              {e.note ? <Typography variant="body2" color="text.secondary">{e.note}</Typography> : null}
            </Box>
          )) : null}
        </Stack>
        <Button sx={{ mt: 2, alignSelf: 'flex-start' }} onClick={() => setDrawerMa(null)}>{t('common.close')}</Button>
      </Drawer>
    </Box>
  );
}
